import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { number, shape, string } from 'prop-types';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { IconSearch } from '../../components';
import { parseSearchFormValuesToParams } from '../../util/search';
import { createResourceLocatorString } from '../../util/routes';
import { withViewport } from '../../util/uiHelpers';
import TopbarSearchForm from '../Topbar/TopbarSearchForm/TopbarSearchForm';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import defaultLocations from '../../config/configDefaultLocationSearches';
import bus, { AuthEventTypes } from '../../modules/bus';
import DisciplineSlider from '../DisciplineSlider/DisciplineSlider';
import CarouselHint from '../CarouselHint/CarouselHint';
import { useConfiguration } from '../../context/configurationContext';
import { userLocation, locationBounds } from '../../util/maps';
import { BOUNDS_BY_PARTNER_KEY } from '../../util/partner';
import PartnerService from '../../services/PartnerService';

import css from './SectionHero.module.css';
import { createURLPaths } from '../../constants/listingAttributes';

const MAX_MOBILE_SCREEN_WIDTH = 768;

const SectionHero = props => {
  const {
    rootClassName,
    className,
    history,
    viewport,
    onManageDisableScrolling,
    titleText,
    subtitleText,
  } = props;

  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();

  const config = useConfiguration();

  const searchFormContainerRef = useRef();
  const searchFormRef = useRef();

  const classes = classNames(rootClassName || css.root, className);

  const isMobileLayout = useMemo(() => viewport.width < MAX_MOBILE_SCREEN_WIDTH, [viewport.width]);

  const handleSubmit = useCallback(
    formValues => {
      const formValuesAsParams = parseSearchFormValuesToParams(formValues);
      const location = defaultLocations[0].predictionPlace;
      const searchParams = { ...location, ...formValuesAsParams };

      const paths = createURLPaths(formValuesAsParams);

      history.push(
        createResourceLocatorString('SearchPage', routeConfiguration, paths, searchParams)
      );
    },
    [history, routeConfiguration]
  );

  const handleExploreActivities = useCallback(
    async e => {
      e.preventDefault();

      // Use the same distance as location input (30km = 30000 meters)
      const currentLocationBoundsDistance = config.maps?.search?.currentLocationBoundsDistance || 30000;

      try {
        // Request user location with same options as LocationAutocompleteInput
        const latlng = await userLocation({
          enableHighAccuracy: false,
          maximumAge: 3600000, // 1 hour cache
        });

        // Create bounds around user location (same as location input does)
        const bounds = locationBounds(latlng, currentLocationBoundsDistance);

        const searchParams = {
          address: '', // Empty address like location input does for current location
          origin: latlng,
          bounds: bounds,
        };

        history.push(
          createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams)
        );
      } catch (error) {
        // Geolocation failed (denied, unavailable, timeout, etc.)
        // Fall back to partner bounds if on partner subdomain, otherwise use default Alps location
        console.warn('[SectionHero] Geolocation failed, using fallback location:', error.message);
        
        const { key: partnerKey } = PartnerService.partner || {};
        const partnerBounds = BOUNDS_BY_PARTNER_KEY[partnerKey];
        
        // Use partner bounds if available (partner subdomain), otherwise use default Alps
        const fallbackLocation = partnerBounds 
          ? { bounds: partnerBounds }
          : defaultLocations[0].predictionPlace;

        history.push(
          createResourceLocatorString('SearchPage', routeConfiguration, {}, fallbackLocation)
        );
      }
    },
    [config.maps?.search?.currentLocationBoundsDistance, history, routeConfiguration]
  );

  const searchFormRefCallback = useCallback(node => (searchFormRef.current = node), []);

  useEffect(() => {
    const handleScroll = () => {
      const { top = 0 } = searchFormContainerRef.current?.getBoundingClientRect() || {};

      // If we are close or after the search form container on hero
      if (top < 0) {
        searchFormRef.current?.classList?.remove(css.collapseTowardsHero);
        searchFormRef.current?.classList?.add(css.collapseTowardsTopbar);
      } else {
        searchFormRef.current?.classList?.remove(css.collapseTowardsTopbar);
        searchFormRef.current?.classList?.add(css.collapseTowardsHero);
      }
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={classes}>
      <div className={css.heroContent}>
        <div
          ref={node => (searchFormContainerRef.current = node)}
          id="SectionHeroFormContainer"
          className={css.titleAndForm}
        >
          <h1 className={css.heroMainTitle}>
            {titleText || <FormattedMessage id="SectionHero.title" />}
          </h1>
          {isMobileLayout ? (
            <button
              type="button"
              onClick={() => bus.broadcastEvent(AuthEventTypes.EXPAND_TOPBAR)}
              className={classNames(css.expandTopbarOnMobile, css.searchForm)}
            >
              <label>{intl.formatMessage({ id: 'General.findAdventure' })}</label>
              <span>
                <IconSearch />
              </span>
            </button>
          ) : (
            <TopbarSearchForm
              onSubmit={handleSubmit}
              searchFormRef={searchFormRefCallback}
              className={css.searchForm}
              isExpandedByDefault
              isMobileLayout={isMobileLayout}
              onManageDisableScrolling={onManageDisableScrolling}
              formId="SectionHeroSearchForm"
            />
          )}
        </div>
        <div className={css.disciplinesContainer}>
          <DisciplineSlider className={css.disciplines} />
          <CarouselHint targetSelector='[data-carousel-wrapper="discipline-slider"] [data-carousel-items]' isMobile={isMobileLayout} />
        </div>
        <div className={css.callToActionArea}>
          <h2 className={css.heroSubTitle}>
            {subtitleText || (
              <FormattedMessage
                id="SectionHero.subTitle"
                values={{ marketplaceName: config.marketplaceName }}
              />
            )}
          </h2>

          {/* <div className={css.spacer}></div> */}

          <button onClick={handleExploreActivities} className={css.heroButton}>
            <FormattedMessage id="General.browse" />
          </button>
        </div>
      </div>
    </div>
  );
};

SectionHero.defaultProps = {
  rootClassName: null,
  className: null,
  titleText: null,
  subtitleText: null,
};

SectionHero.propTypes = {
  rootClassName: string,
  className: string,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
  titleText: string,
  subtitleText: string,
};

export default withViewport(SectionHero);
